//================================================================
//  Tab: Allocations
//================================================================

//  Purpose: This tab displays all the Allocations information related to this agreement

//  Example:
//    <Allocations></Allocations>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

//Contexts
import { GetUser, GetRoles, SetToast } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import LiveField from '../../../../../Components/LiveField/LiveField';
import KebabDropdown from '../../../../../Components/KebabDropdown/KebabDropdown';
import Tooltip from '../../../../../Components/Tooltip/Tooltip';
import AddPOLineItemPane from '../SidePanes/AddPOLineItemPane';
import EditPOLineItemPane from '../SidePanes/EditPOLineItemPane';

//Functions
import DocumentListener from '../../../../../Library/DocumentListener';
import CheckInventoryRole from '../../../../../Library/CheckInventoryRole';

//Images
import Add from '../../../../../Components/Images/Icon_Add_Teal.svg';
import WriteDocument from '../../../../../Library/WriteDocument';


export default function Allocations() {

    //------------------------------------------------------
    //  useContext & React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);
    const setToast = useContext(SetToast);
    const params = useParams();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');

    // Role checks
    const [roleStatus, setRoleStatus] = useState('read'); // read, download, create, write & admin

    // Holds the application document from 
    const [agreementDoc, setAgreementDoc] = useState();

    // Used to toggle the visibility of the add PO line item pane
    const [addPOLineItemPaneOpen, setAddPOLineItemPaneOpen] = useState(false);

    // Used to toggle the visibility of the edit PO line item pane
    const [editPOLineItemPaneOpen, setEditPOLineItemPaneOpen] = useState(false);

    // Used to store the selected PO Line Item
    const [selectedPOLineItemIndex, setSelectedPOLineItemIndex] = useState(null);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handle edit button click and set the selected PO line item
    const handlePOItemEditClick = (polineitemIndex) => {

        // Set the selected PO Line Item Index
        setSelectedPOLineItemIndex(polineitemIndex);

        // Open the Edit PO Line Item Pane
        setEditPOLineItemPaneOpen(true);

    };

    // Handle delete button click and set the selected PO line item
    const handlePOItemDeleteClick = (polineitemIndex) => {

        const updatedPOLineItems = agreementDoc?.polineitems.filter((item, index) => index !== polineitemIndex);

        // Update the document
        const updatedDoc = {
            'polineitems': updatedPOLineItems,
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
        };

        WriteDocument('agreements', agreementDoc?.agreementid, updatedDoc, true)
            .catch((error) => {

                console.log('error', error);

                setToast({
                    'type': 'error',
                    'message': 'Failed to delete PO Line Item',
                });


            })
    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Query Listener
    // - Get Firestore document
    useEffect(() => {

        const agreementid = params.id;

        if (agreementid === '') return;
        if (agreementid === null) return;
        if (getRoles === undefined) return;

        // ============================================
        //  Perform role check
        // ============================================

        const roleStatus = CheckInventoryRole('agreements', getRoles, getUser);
        if (roleStatus === 'access-denied') return setPageStatus('access-denied');
        setRoleStatus(roleStatus);

        // ============================================
        //  Fetch Data
        // ============================================

        function onLoadChange(document) {

            if (document === undefined) return  setPageStatus('forbidden');

            setAgreementDoc(document);
            setPageStatus('onload');

        }

        function onError(error) {

            console.log(error);
            setPageStatus('error-fatal');

        }

        const unsubscribe = DocumentListener('agreements', agreementid, onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

        // eslint-disable-next-line
    }, [getUser, getRoles]);


    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            body={
                <div className='flex flex-col px-[20px]'>

                    {/* PO Number (eFinance) */}
                    <LiveField
                        type={'text'}
                        collectionid={'agreements'}
                        documentid={agreementDoc?.agreementid}
                        fieldpath={'ponumber'}
                        defaultValue={agreementDoc?.ponumber}
                        placeholder={'Enter PO Number'}
                        label={<label className='font-medium'> PO Number (eFinance) </label>}
                        disabled={false}
                    ></LiveField>

                    <div className='FormComponent-Row'>
                        <label className='font-medium'> Setup PO Line Items </label>

                        {/*  PO Line Table */}
                        <div className='border border-[#D8D8D8] rounded'>
                            <table className='table-fixed w-full max-h-96 overflow-y-scroll'>
                                <thead>
                                    <tr className='border-b-[#D8D8D8] border-solid border-b'>
                                        <th className='py-[15px] px-[20px] font-medium text-sm'>Project Code</th>
                                        <th className='py-[15px] px-[8px] font-medium text-sm'>Task Number</th>
                                        <th className='py-[15px] px-[8px] font-medium text-sm'>Expenditure Type</th>
                                        <th className='py-[15px] px-[20px] font-medium text-sm'>Currency</th>
                                        <th className='py-[15px] px-[20px] font-medium text-sm'>PO Amount (exTax)</th>
                                        <th className='py-[15px] px-[20px] font-medium text-sm'>Tax rate (%)</th>
                                        <th className='py-[15px] px-[8px] font-medium text-sm'>Fiscal Year</th>
                                        <th className='py-[15px] px-[8px] font-medium text-sm'>Description</th>

                                        <th className='py-[15px] px-[15px] font-medium text-sm min-w-20'>
                                            {
                                                // Role check for adding PO line items
                                                (roleStatus === 'admin' || roleStatus === 'delete' || roleStatus === 'write') &&
                                                <Tooltip
                                                    message={'+ Add PO item'}
                                                    children={<img className='cursor-pointer' src={Add} alt='add' onClick={() => setAddPOLineItemPaneOpen(true)}></img>}
                                                ></Tooltip>
                                            }
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {

                                        // No PO Line Items Found
                                        agreementDoc?.polineitems?.length === 0 || agreementDoc?.polineitems === undefined
                                            ?
                                            (
                                                <tr>
                                                    <td colSpan={9}>
                                                        <div className='text-center p-[2%]' style={{visibility: (roleStatus === 'admin' || roleStatus === 'delete' || roleStatus === 'write') ? 'visible' : 'hidden' }}>
                                                            Click on the '+' icon to add a new PO Line Item.
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                            :
                                            (

                                                // Map in each PO Line Item
                                                agreementDoc?.polineitems?.map((poLine, index) => (
                                                    <tr
                                                        key={index}
                                                        className='border-b-[#C8C8C9] border-solid border-b last:border-0 hover:bg-[#F0F7F7]'
                                                    >

                                                        {/* Project Code */}
                                                        <td className='py-[15px] px-[20px] text-sm'>{poLine?.projectcode}</td>

                                                        {/* Task Number */}
                                                        <td className='py-[15px] px-[8px] text-sm'>
                                                            {poLine?.tasknumber}
                                                        </td>

                                                        {/* Expenditure Type */}
                                                        <td className='py-[15px] px-[8px] text-sm'>{poLine?.expendituretype}</td>

                                                        {/* Currency */}
                                                        <td className='py-[15px] px-[20px] text-sm'>{poLine?.currency}</td>

                                                        {/* PO Amount */}
                                                        <td className='py-[15px] px-[20px] text-sm'>{poLine?.poamount}</td>

                                                        {/* Tax Rate */}
                                                        <td className='py-[15px] px-[20px] text-sm'>{poLine?.taxrate}</td>

                                                        {/* Fiscal Year */}
                                                        <td className='py-[15px] px-[8px] text-sm'>{poLine?.fiscalyear}</td>

                                                        {/* Description */}
                                                        <td className='py-[15px] px-[8px] text-sm'>{poLine?.description}</td>

                                                        {/* Dropdown with Edit and Remove options */}
                                                        <td className='py-[15px] px-[15px]'>
                                                            {
                                                                (roleStatus === 'admin' || roleStatus === 'delete' || roleStatus === 'write') &&
                                                                <KebabDropdown>
                                                                    <div className=' bg-white  rounded shadow-lg z-10'>
                                                                        <button
                                                                            onClick={() => handlePOItemEditClick(index)}
                                                                            className='block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100'
                                                                        >
                                                                            Edit
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handlePOItemDeleteClick(index)}
                                                                            className='block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100'
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    </div>
                                                                </KebabDropdown>

                                                            }
                                                        </td>
                                                    </tr>
                                                ))
                                            )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ===================================================== */}
                    {/*  Add PO Line Item Pane                                */}
                    {/* ===================================================== */}

                    <AddPOLineItemPane
                        agreement={agreementDoc}
                        addPOLineItemPaneOpen={addPOLineItemPaneOpen}
                        setAddPOLineItemPaneOpen={setAddPOLineItemPaneOpen}
                    ></AddPOLineItemPane>

                    {/* ===================================================== */}
                    {/*  Edit PO Line Item Pane                               */}
                    {/* ===================================================== */}

                    {
                        editPOLineItemPaneOpen &&
                        (
                            <EditPOLineItemPane
                                agreement={agreementDoc}
                                editPOLineItemPaneOpen={editPOLineItemPaneOpen}
                                setEditPOLineItemPaneOpen={setEditPOLineItemPaneOpen}
                                polineitemIndex={selectedPOLineItemIndex}
                            ></EditPOLineItemPane>
                        )
                    }

                </div>
            }
        ></PageComponent>
    )
}
